/**
 * The envelope's "Future register" card (TMMAPP-0101; audit 06 § B3, v2 L1570–1640): the recurring
 * allocations and the plan lines together, in date order, over the same projection as the chart.
 *
 * - `ListTableHeader` Date 62 / Event / Source 116 / Amount 92 / Balance 96, then a `surface2` "Balance
 *   today" row; below `md` the rows stack.
 * - The Source pill reads the served `sourceKind` directly: Allocated (the 0108 `brandSoft` / `text` wash), Budget plan
 *   and One time (warning), Envelope (`successSoft` / `successText`); a row with none shows no pill. An
 *   Allocated row's second line is "{source account} · {rule}" from the served `sourceAccountName` and
 *   `rule` (owner only; absent for everyone else).
 * - A writer's live row has a "×" dismiss (26), a skipped one an outlined "Restore", through the
 *   plan-line or series APIs. A published row (TMMAPI-0150: a dated amount under its served name, with
 *   no series, plan line, source account or mode) offers no actions at all.
 */
import { Box, Button, IconButton, Paper, Stack, Typography, useTheme } from '@mui/material';
import { MoneyParts } from '@/components/MoneyText';
import { registerAmountColor } from '@/components/moneyTone';
import { useTranslation } from 'react-i18next';
import type { ForecastSliceRule, RegisterRow } from '@/api/projections';
import { CloseIcon } from '@/components/icons';
import { ListTableHeader } from '@/components/ListTableHeader';
import { Pill } from '@/components/Pill';
import { bpToPercentText, formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import { actionKey, futureRows, isActionable, isSkipped } from './futureRows';

const COLUMNS = '62px minmax(0, 1fr) 116px 92px 96px 30px';
const DATE_SHORT = { month: 'short', day: 'numeric' } as const;

interface FutureRegisterProps {
  rows: RegisterRow[];
  /** The "Balance today" row's date and balance. */
  from: string;
  startMinor: number | null;
  currency: string | null;
  wide: boolean;
  canWrite: boolean;
  busyKey: string | null;
  onDismiss: (row: RegisterRow) => void;
  onRestore: (row: RegisterRow) => void;
}

function RulePhrase({ rule, currency }: { rule: ForecastSliceRule; currency: string | null }) {
  const { t } = useTranslation();
  const locale = useLocale();
  if (rule.mode === 'FIXED' && rule.amountMinor !== null) {
    return <MoneyParts minor={rule.amountMinor} currency={currency} />;
  }
  if (rule.mode === 'PERCENT' && rule.bp !== null) return <>{bpToPercentText(rule.bp, locale)}</>;
  if (rule.mode === 'REMAINDER') return <>{t('forecast.ruleRemainder')}</>;
  return null;
}

function SourcePill({ row }: { row: RegisterRow }) {
  const { t } = useTranslation();
  if (row.sourceKind === null) return null;
  const label = t(`forecast.source.${row.sourceKind}`);
  if (row.sourceKind === 'ALLOCATED') {
    return (
      <Pill wash="brand" data-paint="allocated">
        {label}
      </Pill>
    );
  }
  return (
    <Pill
      tone={row.sourceKind === 'ENVELOPE' ? 'success' : 'warning'}
      data-paint={row.sourceKind.toLowerCase()}
    >
      {label}
    </Pill>
  );
}

export function FutureRegister({
  rows,
  from,
  startMinor,
  currency,
  wide,
  canWrite,
  busyKey,
  onDismiss,
  onRestore,
}: FutureRegisterProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const listed = futureRows(rows);
  const money = (minor: number | null) =>
    minor === null ? '—' : <MoneyParts minor={minor} currency={currency} />;
  const signed = (minor: number | null) =>
    minor === null ? '—' : <MoneyParts minor={minor} currency={currency} signed />;
  const toneOf = (minor: number | null) =>
    minor !== null && minor < 0 ? tokens.moneyNegative : tokens.text2;

  const action = (row: RegisterRow, name: string, date: string) => {
    if (!canWrite || !isActionable(row)) return <Box sx={{ width: 26 }} />;
    const busy = busyKey === actionKey(row);
    if (isSkipped(row)) {
      return (
        <Button
          variant="outlined"
          disabled={busy}
          aria-label={t('forecast.restoreRow', { name, date })}
          onClick={() => onRestore(row)}
          sx={{ minHeight: 0, p: '3px 10px', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap' }}
        >
          {t('planLines.restore')}
        </Button>
      );
    }
    return (
      <IconButton
        disabled={busy}
        aria-label={t('forecast.dismissRow', { name, date })}
        onClick={() => onDismiss(row)}
        sx={{ width: 26, height: 26, p: 0, color: tokens.text2 }}
      >
        <CloseIcon size={14} />
      </IconButton>
    );
  };

  const subline = (row: RegisterRow) =>
    row.sourceKind === 'ALLOCATED' && row.sourceAccountName !== null ? (
      <Box
        component="span"
        data-forecast-source=""
        sx={{ display: 'block', fontSize: 11, mt: '1px', color: tokens.text2 }}
      >
        {row.sourceAccountName}
        {row.rule !== null && (
          <>
            {' · '}
            <RulePhrase rule={row.rule} currency={currency} />
          </>
        )}
      </Box>
    ) : null;

  return (
    <Paper
      component="section"
      aria-labelledby="future-register-title"
      sx={{ p: 0, overflow: 'hidden' }}
    >
      <Stack
        direction="row"
        data-card-header=""
        sx={{
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          p: '16px 20px',
          borderBottom: `1px solid ${tokens.line}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography id="future-register-title" variant="h3" component="h2">
            {t('forecast.futureTitle')}
          </Typography>
          <Typography variant="caption" component="p" sx={{ color: tokens.text2 }}>
            {t('forecast.futureSub')}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: tokens.text2 }}>
          {t('virtualAccount.forecast.count', { count: listed.length })}
        </Typography>
      </Stack>
      {wide && (
        <ListTableHeader columns={COLUMNS}>
          <span>{t('forecast.colDate')}</span>
          <span>{t('forecast.colEvent')}</span>
          <span>{t('forecast.colSource')}</span>
          <Box component="span" sx={{ textAlign: 'end' }}>
            {t('forecast.colAmount')}
          </Box>
          <Box component="span" sx={{ textAlign: 'end' }}>
            {t('envelope.colBalance')}
          </Box>
          <span />
        </ListTableHeader>
      )}
      <Box
        data-forecast-start=""
        sx={{
          display: wide ? 'grid' : 'flex',
          flexDirection: 'column',
          gridTemplateColumns: COLUMNS,
          columnGap: 2,
          gap: wide ? undefined : '3px',
          alignItems: wide ? 'center' : 'flex-start',
          p: wide ? '10px 20px' : '14px 16px',
          bgcolor: tokens.surface2,
          borderBottom: `1px solid ${tokens.line}`,
        }}
      >
        <Box
          component="span"
          sx={{ fontSize: 12, color: tokens.text2, fontVariantNumeric: 'tabular-nums' }}
        >
          {formatCivilDate(from, locale, DATE_SHORT)}
        </Box>
        <Box component="span" sx={{ fontSize: 13, fontWeight: 500 }}>
          {t('forecast.balanceToday')}
        </Box>
        {wide && <span />}
        {wide && <span />}
        <Box
          component="span"
          sx={{
            textAlign: wide ? 'end' : 'start',
            fontSize: wide ? 13 : 18,
            fontWeight: wide ? 500 : 400,
            fontVariantNumeric: 'tabular-nums',
            color: startMinor !== null && startMinor < 0 ? tokens.moneyNegative : tokens.text,
          }}
        >
          {money(startMinor)}
        </Box>
        {wide && <span />}
      </Box>
      {listed.map((row, index) => {
        const key = `${actionKey(row)}|${index}`;
        const skipped = isSkipped(row);
        const name = row.payee ?? row.note ?? row.envelopeName ?? '—';
        const dateText = formatCivilDate(row.occursOn ?? row.date, locale, DATE_SHORT);
        const strike = skipped ? { textDecoration: 'line-through' } : {};
        const amountColour =
          row.amountMinor === null ? tokens.text2 : registerAmountColor(tokens, row.amountMinor);
        return wide ? (
          <Box
            key={key}
            data-forecast-row=""
            sx={{
              display: 'grid',
              gridTemplateColumns: COLUMNS,
              columnGap: 2,
              alignItems: 'center',
              p: '9px 20px',
              borderBottom: `1px solid ${tokens.line}`,
              '&:hover': { bgcolor: tokens.hover },
            }}
          >
            <Box
              component="span"
              sx={{ fontSize: 12, color: tokens.text2, fontVariantNumeric: 'tabular-nums' }}
            >
              {dateText}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box
                component="span"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 13,
                  fontWeight: 500,
                  color: skipped ? tokens.text2 : tokens.text,
                  ...strike,
                }}
              >
                {name}
              </Box>
              {subline(row)}
            </Box>
            <Box>
              <SourcePill row={row} />
            </Box>
            <Box
              component="span"
              sx={{
                textAlign: 'end',
                fontSize: 13,
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
                color: skipped ? tokens.text2 : amountColour,
                ...strike,
              }}
            >
              {signed(row.amountMinor)}
            </Box>
            <Box
              component="span"
              sx={{
                textAlign: 'end',
                fontSize: 13,
                fontVariantNumeric: 'tabular-nums',
                color: toneOf(row.runningBalanceMinor),
              }}
            >
              {money(row.runningBalanceMinor)}
            </Box>
            {action(row, name, formatCivilDate(row.occursOn ?? row.date, locale))}
          </Box>
        ) : (
          <Stack
            key={key}
            data-forecast-row=""
            sx={{ gap: 1, p: '14px 16px', borderBottom: `1px solid ${tokens.line}` }}
          >
            <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1.25 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    fontSize: 15,
                    fontWeight: 500,
                    color: skipped ? tokens.text2 : tokens.text,
                    ...strike,
                  }}
                >
                  {name}
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    fontSize: 12,
                    color: tokens.textLabel('surface'),
                    mt: '2px',
                  }}
                >
                  {dateText}
                </Box>
                {subline(row)}
              </Box>
              <SourcePill row={row} />
              {action(row, name, formatCivilDate(row.occursOn ?? row.date, locale))}
            </Stack>
            <Stack direction="row" sx={{ gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                <Typography variant="micro" sx={{ color: tokens.textLabel('surface') }}>
                  {t('forecast.colAmount')}
                </Typography>
                <Box
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    fontVariantNumeric: 'tabular-nums',
                    color: skipped ? tokens.text2 : amountColour,
                    ...strike,
                  }}
                >
                  {signed(row.amountMinor)}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                <Typography variant="micro" sx={{ color: tokens.textLabel('surface') }}>
                  {t('envelope.colBalance')}
                </Typography>
                <Box
                  sx={{
                    fontSize: 14,
                    fontVariantNumeric: 'tabular-nums',
                    color: toneOf(row.runningBalanceMinor),
                  }}
                >
                  {money(row.runningBalanceMinor)}
                </Box>
              </Box>
            </Stack>
          </Stack>
        );
      })}
      {listed.length === 0 && (
        <Typography
          data-forecast-empty=""
          sx={{ p: '26px 20px', fontSize: 13, lineHeight: 1.6, maxWidth: 560, color: tokens.text2 }}
        >
          {t('forecast.futureEmpty')}
        </Typography>
      )}
    </Paper>
  );
}
